import "@xyflow/react/dist/style.css"
import { type GraphIssue, NODE_CATEGORIES, type NodeType } from "@velizhanin-dev/bot-scenario"
import {
	applyEdgeChanges,
	applyNodeChanges,
	Background,
	BackgroundVariant,
	type Connection,
	type Edge,
	type EdgeChange,
	MiniMap,
	type NodeChange,
	NodeToolbar,
	type NodeTypes,
	Position,
	ReactFlow,
	useReactFlow,
} from "@xyflow/react"
import { Copy, Pencil, Trash2 } from "lucide-react"
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react"
import type { ScenarioDraft } from "@/entities/bot-builder"
import {
	type BlockNode,
	CATEGORY_APPEARANCE,
	EDGE_COLOR,
	NODE_WIDTH,
	type NodeStat,
	toFlowElements,
} from "@/entities/bot-builder"
import type { DraftActions } from "@/features/edit-scenario-draft"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { ToolButton } from "@/shared/ui/tool-button"
import { BlockNodeView } from "./block-node"

const nodeTypes: NodeTypes = { block: BlockNodeView }

/** Тема xyflow через его CSS-переменные: связи, миникарта, фон. */
const CANVAS_THEME = {
	"--xy-background-color": "transparent",
	// Связи нейтральные; выбранная и протягиваемая красные, в цвет главного действия панели.
	"--xy-edge-stroke": EDGE_COLOR,
	"--xy-edge-stroke-selected": "var(--primary)",
	"--xy-edge-stroke-width": "2",
	"--xy-connectionline-stroke": "var(--primary)",
	"--xy-connectionline-stroke-width": "2",
	"--xy-minimap-background-color": "var(--card)",
	"--xy-minimap-mask-background-color": "oklch(0.1 0 0 / 0.5)",
	"--xy-attribution-background-color": "transparent",
} as CSSProperties

/** Запрос «показать блок»: seq меняется на каждый клик, даже по тому же блоку. */
export interface FocusRequest {
	nodeId: string
	seq: number
}

/** Живёт внутри ReactFlow: там доступен его стор с зумом и размерами. */
function FocusController({ request, nodes }: { request: FocusRequest | null; nodes: BlockNode[] }) {
	const { setCenter, getZoom } = useReactFlow()
	const handled = useRef(0)

	useEffect(() => {
		if (!request || request.seq === handled.current) return
		const node = nodes.find((item) => item.id === request.nodeId)
		// До замера блока xyflow ещё не сделал fitView, он перебил бы центрирование.
		if (!node?.measured?.height) return
		handled.current = request.seq
		const height = node.measured.height
		void setCenter(node.position.x + NODE_WIDTH / 2, node.position.y + height / 2, {
			zoom: Math.max(getZoom(), 0.8),
			duration: 300,
		})
	}, [request, nodes, setCenter, getZoom])

	return null
}

interface ScenarioCanvasProps {
	draft: ScenarioDraft
	flowId: string
	issuesByNode: Map<string, GraphIssue[]>
	selectedNodeId: string | null
	onSelectNode: (nodeId: string | null) => void
	actions: Pick<
		DraftActions,
		"putEdge" | "deleteEdge" | "savePositions" | "copyNode" | "deleteNode"
	>
	focusRequest?: FocusRequest | null
	activeNodeIds?: ReadonlySet<string>
	/** «Прошли / Застряли» на блоках: в разделе бота. */
	nodeStats?: ReadonlyMap<string, NodeStat>
	/** Телефон: только смотреть и выбирать: блоки не двигаются, связи ставятся в панели блока. */
	compact?: boolean
}

/**
 * Холст одного потока. Состояние: черновик в кэше; xyflow держит только перетаскивание и размеры.
 * ReactFlowProvider, у родителя: панель инструментов над холстом тоже управляет зумом.
 */
export function ScenarioCanvas({
	draft,
	flowId,
	issuesByNode,
	selectedNodeId,
	onSelectNode,
	actions,
	focusRequest = null,
	activeNodeIds,
	nodeStats,
	compact = false,
}: ScenarioCanvasProps) {
	// Выбор узла в сборку не входит: клик по узлу не должен пересобирать весь поток.
	const elements = useMemo(
		() => toFlowElements(draft, flowId, issuesByNode, null, activeNodeIds, nodeStats),
		[draft, flowId, issuesByNode, activeNodeIds, nodeStats],
	)
	const [nodes, setNodes] = useState<BlockNode[]>(elements.nodes)
	const [edges, setEdges] = useState<Edge[]>(elements.edges)
	const selectedRef = useRef(selectedNodeId)
	selectedRef.current = selectedNodeId

	// Черновик поменялся: берём его, сохраняя замеры xyflow, чтобы блоки не мигали.
	useEffect(() => {
		setNodes((previous) => {
			const measured = new Map(previous.map((node) => [node.id, node.measured]))
			return elements.nodes.map((node) => ({
				...node,
				measured: measured.get(node.id),
				selected: node.id === selectedRef.current,
			}))
		})
	}, [elements.nodes])
	// Сменился выбор: меняем флаг только у двух узлов, остальные объекты те же и не перерисовываются.
	useEffect(() => {
		setNodes((previous) =>
			previous.map((node) => {
				const selected = node.id === selectedNodeId
				return node.selected === selected ? node : { ...node, selected }
			}),
		)
	}, [selectedNodeId])
	useEffect(() => {
		setEdges((previous) => {
			const selected = new Set(previous.filter((edge) => edge.selected).map((edge) => edge.id))
			return elements.edges.map((edge) => ({ ...edge, selected: selected.has(edge.id) }))
		})
	}, [elements.edges])

	const onNodesChange = (changes: NodeChange<BlockNode>[]) => {
		// Выбор блока ведёт редактор, удаление: только из панели блока.
		const allowed = changes.filter((change) => change.type !== "select" && change.type !== "remove")
		if (allowed.length > 0) setNodes((current) => applyNodeChanges(allowed, current))
	}

	const onEdgesChange = (changes: EdgeChange[]) => {
		const allowed = changes.filter((change) => change.type !== "remove")
		if (allowed.length > 0) setEdges((current) => applyEdgeChanges(allowed, current))
	}

	const onConnect = (connection: Connection) => {
		if (!connection.sourceHandle || connection.source === connection.target) return
		void actions.putEdge({
			sourceNodeId: connection.source,
			sourcePort: connection.sourceHandle,
			targetNodeId: connection.target,
		})
	}

	const { confirm } = useConfirm()
	const selectedType = draft.nodes.find((node) => node.id === selectedNodeId)?.type
	const selectedEdges = edges.filter((edge) => edge.selected)
	const deleteSelectedEdges = (toDelete: Edge[]) => {
		for (const edge of toDelete) void actions.deleteEdge(edge.id)
	}

	return (
		<div className="relative h-full w-full">
			<ReactFlow<BlockNode, Edge>
				key={flowId}
				nodes={nodes}
				edges={edges}
				nodeTypes={nodeTypes}
				colorMode="dark"
				style={CANVAS_THEME}
				fitView
				fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
				minZoom={0.15}
				maxZoom={1.5}
				deleteKeyCode={compact ? null : ["Backspace", "Delete"]}
				nodesDraggable={!compact}
				nodesConnectable={!compact}
				elementsSelectable={!compact}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				onEdgesDelete={deleteSelectedEdges}
				onConnect={onConnect}
				onNodeClick={(_, node) => onSelectNode(node.id)}
				onPaneClick={() => onSelectNode(null)}
				onNodeDragStop={(_, __, dragged) =>
					void actions.savePositions(
						dragged.map((node) => ({
							nodeId: node.id,
							x: Math.round(node.position.x),
							y: Math.round(node.position.y),
						})),
					)
				}
			>
				<FocusController request={focusRequest} nodes={nodes} />
				<Background variant={BackgroundVariant.Dots} gap={22} size={1.2} color="oklch(1 0 0 / 9%)" />
				{/* Миникарта нужна только большой воронке: на нескольких блоках это лишний прямоугольник. */}
				{nodes.length > 12 ? (
					<MiniMap
						pannable
						zoomable
						position="top-right"
						className="!hidden overflow-hidden rounded-2xl border border-border shadow-xl xl:!block"
						style={{ width: 168, height: 108 }}
						nodeBorderRadius={12}
						nodeColor={(node) =>
							CATEGORY_APPEARANCE[
								NODE_CATEGORIES[(node as BlockNode).data.node.type as NodeType] ?? "action"
							].minimap
						}
					/>
				) : null}
				{selectedNodeId && !compact ? (
					<NodeToolbar nodeId={selectedNodeId} position={Position.Top} offset={8}>
						<div className="flex items-center gap-0.5 rounded-xl border border-border bg-card/95 p-1 shadow-xl backdrop-blur-md">
							<ToolButton label="Изменить блок" className="size-9" onClick={() => onSelectNode(selectedNodeId)}>
								<Pencil />
							</ToolButton>
							{selectedType !== "start" ? (
								<>
									<ToolButton
										label="Копия блока"
										className="size-9"
										onClick={async () => {
											const result = await actions.copyNode(selectedNodeId)
											if (result) onSelectNode(result.node.id)
										}}
									>
										<Copy />
									</ToolButton>
									<ToolButton
										label="Удалить блок"
										className="size-9 hover:bg-red-500/15 hover:text-red-300"
										onClick={() =>
											confirm({
												title: "Удалить блок?",
												message: "Связи блока тоже удалятся.",
												confirmText: "Удалить",
												onConfirm: async () => {
													await actions.deleteNode(selectedNodeId)
													onSelectNode(null)
												},
											})
										}
									>
										<Trash2 />
									</ToolButton>
								</>
							) : null}
						</div>
					</NodeToolbar>
				) : null}
			</ReactFlow>

			{selectedEdges.length > 0 ? (
				<div className="absolute top-3 left-1/2 z-10 -translate-x-1/2">
					<Button
						type="button"
						variant="ghost"
						className="h-9 gap-2 rounded-xl border border-red-500/30 bg-card/90 px-3 py-0 text-sm text-red-300 shadow-xl backdrop-blur hover:bg-red-500/15"
						onClick={() => deleteSelectedEdges(selectedEdges)}
					>
						<Trash2 className="h-4 w-4" />
						{selectedEdges.length > 1 ? `Удалить связи (${selectedEdges.length})` : "Удалить связь"}
					</Button>
				</div>
			) : null}
		</div>
	)
}
